import { getRouteEditProject, getRouteProjects } from "@/zapi-frontend/shared/const/router";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { AppButton, Typography, ConfirmationModal } from "@libs/zapi-ui/custom";
import { InnoveraIcon } from "@libs/zapi-ui/assets";
import { Pencil, Copy } from "lucide-react";
import { ExportButton } from "./_ExportButton";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useOrganizationStore, type Organization } from "@/zapi-frontend/stores/organization";
import { useMediaQuery } from "@/zapi-frontend/shared/hooks/useMediaQuery";
import { DuplicateProjectModal } from "./DuplicateProjectModal";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { NotificationTrigger } from "@/zapi-frontend/features/Notifications";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";

export function ProjectDetailsHeader() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isLabs } = useMeStore();
  const { isFetchProjectLoading, selectedProject, duplicateProject } = useProjectStore();
  const { isSystemAdmin, availableOrganizations, loadOrganizations, switchOrganization } = useOrganizationStore();
  const { setViewMode, projectChapters, setActiveChapter } = useChapterStore();
  const { closeHandler } = useProjectSidebarStore();

  const isLaptop = useMediaQuery("(min-width: 984px)");
  const isTablet = useMediaQuery("(min-width: 720px)");

  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const currentTab = searchParams.get("tab") || "overview";

  useEffect(() => {
    if (isSystemAdmin) loadOrganizations();
  }, [isSystemAdmin, loadOrganizations]);

  useEffect(() => {
    if (!searchParams.get("tab")) setSearchParams({ tab: "overview" }, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleProjectNameClick = () => {
    setViewMode("projectOverview");
    closeHandler();
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("tab", "overview");
      params.delete("chapterId");
      params.delete("view");
      return params;
    });
  };

  const handleDocumentClick = () => {
    const executiveSummaryChapter = projectChapters.find(
      (chapter) => chapter.projectTemplateChapter.slug === "executive-summary"
    );

    if (executiveSummaryChapter) {
      setViewMode("chapter");
      setActiveChapter(executiveSummaryChapter.id.toString());
      closeHandler();
    }

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("tab", "document");
      if (executiveSummaryChapter) {
        params.set("chapterId", executiveSummaryChapter.id.toString());
      }
      return params;
    });
  };

  const handlePresentationClick = () => setSearchParams({ tab: "presentation" });
  const currentOrganization = availableOrganizations.find(
    (org: Organization) => org.id === selectedProject?.organizationId
  );
  const currentOrgName = currentOrganization?.name;

  function handleEditProject() {
    if (!selectedProject) return;
    navigate(getRouteEditProject(selectedProject.id));
  }

  const handleDuplicateProject = async (organizationId?: number) => {
    if (!selectedProject) return;
    const [, error] = await tryCatch<void, AxiosError<{ message: string }>>(
      duplicateProject(selectedProject.id, organizationId)
    );
    if (error) {
      toast.error(error.response?.data.message || "Failed to duplicate project");
      return;
    }

    if (isSystemAdmin && organizationId) switchOrganization(organizationId);

    toast.success("Project duplicated successfully");
    navigate(getRouteProjects());
  };

  return (
    <div className="w-full h-full flex items-center gap-4">
      <Link
        data-testid="header-logo-link"
        to={getRouteProjects()}
        className="w-10 h-10 inline-flex justify-center items-center [&_svg>path]:hover:fill-innoveraOrange-400 [&_svg>path]:transition-[fill] [&_svg>path]:duration-[3ms]"
      >
        <InnoveraIcon className="w-6 h-6" />
      </Link>
      {isFetchProjectLoading && (
        <Typography variant="body2" className="animate-pulse">
          Loading project...
        </Typography>
      )}
      {!isFetchProjectLoading && selectedProject && (
        <div className="grid grid-cols-[1fr_auto] items-center w-full">
          <div className="flex items-center gap-5">
            <Typography
              component="div"
              variant="body1"
              color="zinc900"
              className="inline-flex items-center gap-2 min-w-0"
            >
              {isSystemAdmin && currentOrgName && isTablet && (
                <span className="text-innoveraZinc-500 font-normal mr-1 shrink-0">(org: {currentOrgName})</span>
              )}
              {selectedProject?.logoUrl && (
                <img
                  src={selectedProject?.logoUrl}
                  alt={selectedProject?.name}
                  className="size-5 rounded-[5px] shrink-0"
                />
              )}
            </Typography>

            <div className="flex items-center gap-4 h-[52px] -mb-1">
              <AppButton
                appearance="text"
                onClick={handleProjectNameClick}
                className={cn(
                  "h-full text-[14px] rounded-none border-b border-transparent hover:border-innoveraOrange-300 !bg-transparent min-w-0 max-w-[300px]",
                  currentTab === "overview" && "!border-innoveraOrange-400"
                )}
              >
                <span className="truncate min-w-0">{selectedProject?.name}</span>
              </AppButton>
              <AppButton
                appearance="text"
                onClick={handleDocumentClick}
                className={cn(
                  "h-full text-[14px] rounded-none border-b border-transparent hover:border-innoveraOrange-300 !bg-transparent",
                  currentTab === "document" && "!border-innoveraOrange-400"
                )}
              >
                Document
              </AppButton>
              <AppButton
                appearance="text"
                onClick={handlePresentationClick}
                className={cn(
                  "h-full text-[14px] rounded-none border-b border-transparent hover:border-innoveraOrange-300 !bg-transparent",
                  currentTab === "presentation" && "!border-innoveraOrange-400"
                )}
              >
                Presentation
              </AppButton>
            </div>
          </div>

          <div className="flex items-center laptop:gap-2">
            <AppButton
              appearance="text"
              onClick={handleEditProject}
              data-testid="project-header-dropdown-edit"
              className="px-3 py-2 flex items-center gap-1"
              startAdornment={<Pencil className="text-innoveraZinc-900" />}
            >
              {isLaptop && (
                <Typography variant="label2" color="zinc700">
                  Edit Project
                </Typography>
              )}
            </AppButton>
            <AppButton
              appearance="text"
              onClick={() => setIsDuplicateModalOpen(true)}
              data-testid="project-header-duplicate"
              className="px-3 py-2 flex items-center gap-1"
              startAdornment={<Copy className="text-innoveraZinc-900" />}
            >
              {isLaptop && (
                <Typography variant="label2" color="zinc700">
                  Duplicate Project
                </Typography>
              )}
            </AppButton>
            <ExportButton />
            {isLabs && <NotificationTrigger projectId={id ? parseInt(id) : undefined} />}
            <div className="w-[1px] h-3 bg-innoveraZinc-200 ml-2" />
          </div>
        </div>
      )}
      {isSystemAdmin ? (
        <DuplicateProjectModal
          isOpen={isDuplicateModalOpen}
          onClose={() => setIsDuplicateModalOpen(false)}
          onConfirm={handleDuplicateProject}
          currentOrganizationId={selectedProject?.organizationId}
        />
      ) : (
        <ConfirmationModal
          isOpen={isDuplicateModalOpen}
          onClose={() => setIsDuplicateModalOpen(false)}
          onConfirm={() => handleDuplicateProject()}
          title="Duplicate Project"
          description="Are you sure you want to duplicate this project?"
        />
      )}
    </div>
  );
}
